import { toastNotification } from '../../components/toast/Toast';
import { useDeleteInvitations } from '../../queries/use-delete-invitations';
import { useCreateInvitations } from '../../queries/use-create-invitation';
import { Invitation } from '../../types/invitation';
import plural from '../../utilities/plural';

export function useResendInvitations(): {
    isLoading: boolean;
    resendInvitations: (invitations: Invitation[]) => Promise<void>;
} {
    const deleteInvitationsQuery = useDeleteInvitations({ invalidate: false });
    const createInvitationsQuery = useCreateInvitations();

    function resendInvitations(invitations: Invitation[]): Promise<void> {
        return new Promise((resolve, reject) => {
            deleteInvitationsQuery.mutate(
                { ids: invitations.map((invitation: Invitation) => invitation.id) },
                {
                    onSuccess: () => {
                        createInvitationsQuery.mutate(
                            {
                                invites: invitations.map((invitation: Invitation) => ({
                                    email: invitation.email,
                                    role: invitation.role,
                                })),
                            },
                            {
                                onSuccess: () => {
                                    resolve();
                                    toastNotification(
                                        'success',
                                        `${invitations.length} user ${plural(invitations.length, 'invitation', 'invitations')} resent!`
                                    );
                                },
                                onError: (err) => {
                                    reject(new Error('Unable to resend user invitations!'));
                                    const errorInvites = err.response?.data.results.filter(
                                        (r: { statusCode: number }) => r.statusCode !== 201
                                    );
                                    const successInvites = err.response?.data.results.filter(
                                        (r: { statusCode: number }) => r.statusCode === 201
                                    );

                                    toastNotification(
                                        'success',
                                        `${successInvites.length} user ${plural(
                                            successInvites.length,
                                            'invitation',
                                            'invitations'
                                        )} resent!`
                                    );
                                    toastNotification(
                                        'error',
                                        `${errorInvites.length} user ${plural(
                                            successInvites.length,
                                            'invitation',
                                            'invitations'
                                        )} failed to resend!`
                                    );
                                },
                            }
                        );
                    },
                    onError: () => {
                        reject(new Error('Unable to resend user invitations!'));
                        toastNotification(
                            'error',
                            `${invitations.length} user ${plural(invitations.length, 'invitation', 'invitations')} failed to delete!`
                        );
                    },
                }
            );
        });
    }

    return {
        isLoading: deleteInvitationsQuery.isLoading || createInvitationsQuery.isLoading,
        resendInvitations,
    };
}
